import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'

export type Role = 'member' | 'manager' | 'admin'

export interface AuthUser {
  id: string
  email: string
  username: string | null
  display_name: string
  role: Role
  group_name: string
  region_code: string
  is_group_lead: boolean
  account_status: string
  visible_group_scope: string[] | null
}

interface AuthContextValue {
  user: AuthUser | null
  accessToken: string | null
  isLoading: boolean
  login: (account: string, password: string, remember: boolean) => Promise<void>
  logout: () => Promise<void>
  logoutOthers: () => Promise<void>
  refreshUser: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

const ACCESS_KEY = 'cswd_access'
const REFRESH_KEY = 'cswd_refresh'
const PERSIST_KEY = 'cswd_persist'

function getStoredTokens(): { accessToken: string | null; refreshToken: string | null; persistent: boolean } {
  const persistent = localStorage.getItem(PERSIST_KEY) === '1'
  const store = persistent ? localStorage : sessionStorage
  return {
    accessToken: store.getItem(ACCESS_KEY),
    refreshToken: store.getItem(REFRESH_KEY),
    persistent,
  }
}

function storeTokens(accessToken: string, refreshToken: string, persistent: boolean) {
  const store = persistent ? localStorage : sessionStorage
  localStorage.setItem(PERSIST_KEY, persistent ? '1' : '0')
  store.setItem(ACCESS_KEY, accessToken)
  store.setItem(REFRESH_KEY, refreshToken)
  if (!persistent) {
    localStorage.removeItem(ACCESS_KEY)
    localStorage.removeItem(REFRESH_KEY)
  }
}

function clearTokens() {
  localStorage.removeItem(ACCESS_KEY)
  localStorage.removeItem(REFRESH_KEY)
  localStorage.removeItem(PERSIST_KEY)
  sessionStorage.removeItem(ACCESS_KEY)
  sessionStorage.removeItem(REFRESH_KEY)
}

function _getRefreshDelay(token: string): number {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]))
    const remaining = payload.exp * 1000 - Date.now()
    return Math.max(remaining - 60_000, 5_000)
  } catch {
    return 5_000
  }
}

async function apiFetch(path: string, opts: RequestInit = {}) {
  return fetch(path, opts)
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [accessToken, setAccessToken] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const doRefresh = useCallback(async (rt: string, persistent: boolean): Promise<string | null> => {
    const r = await apiFetch('/api/auth/refresh', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: rt }),
    })
    if (!r.ok) return null
    const data = await r.json()
    const newAt = data.access_token as string
    storeTokens(newAt, rt, persistent)
    return newAt
  }, [])

  const fetchMe = useCallback(async (token: string): Promise<AuthUser | null> => {
    try {
      const r = await apiFetch('/api/auth/me', { headers: { Authorization: `Bearer ${token}` } })
      if (r.ok) return (await r.json()) as AuthUser
    } catch { /* ignore */ }
    return null
  }, [])

  const scheduleRefresh = useCallback((at: string, rt: string, persistent: boolean) => {
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current)
    const delay = _getRefreshDelay(at)
    refreshTimerRef.current = setTimeout(async () => {
      const newAt = await doRefresh(rt, persistent)
      if (newAt) {
        setAccessToken(newAt)
        scheduleRefresh(newAt, rt, persistent)
      } else {
        setUser(null)
        setAccessToken(null)
        clearTokens()
      }
    }, delay)
  }, [doRefresh])

  // On mount: restore session from stored tokens
  useEffect(() => {
    const { accessToken: at, refreshToken: rt, persistent } = getStoredTokens()
    if (!at || !rt) {
      setIsLoading(false)
      return
    }

    fetchMe(at).then(async (u) => {
      if (u) {
        setUser(u)
        setAccessToken(at)
        scheduleRefresh(at, rt, persistent)
      } else {
        // Try refresh
        const newAt = await doRefresh(rt, persistent)
        if (newAt) {
          setAccessToken(newAt)
          const u2 = await fetchMe(newAt)
          if (u2) {
            setUser(u2)
            scheduleRefresh(newAt, rt, persistent)
          } else {
            clearTokens()
          }
        } else {
          clearTokens()
        }
      }
    }).finally(() => {
      setIsLoading(false)
    })
  }, [fetchMe, doRefresh, scheduleRefresh])

  const login = useCallback(async (account: string, password: string, remember: boolean) => {
    const r = await apiFetch('/api/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ account, password, remember }),
    })
    if (!r.ok) {
      const err = await r.json()
      const e = new Error(err?.error?.message ?? '登录失败') as Error & { status: number; code: string }
      e.status = r.status
      e.code = err?.error?.code
      throw e
    }
    const data = await r.json()
    const at = data.access_token as string
    const rt = data.refresh_token as string
    storeTokens(at, rt, remember)
    setAccessToken(at)
    setUser(data.member as AuthUser)
    scheduleRefresh(at, rt, remember)
  }, [scheduleRefresh])

  const logout = useCallback(async () => {
    const { refreshToken } = getStoredTokens()
    if (refreshToken) {
      try {
        await apiFetch('/api/auth/logout', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refresh_token: refreshToken }),
        })
      } catch { /* ignore */ }
    }
    clearTokens()
    setUser(null)
    setAccessToken(null)
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current)
  }, [])

  const logoutOthers = useCallback(async () => {
    const { refreshToken } = getStoredTokens()
    if (!refreshToken || !accessToken) return
    await apiFetch('/api/auth/logout-others', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
      body: JSON.stringify({ refresh_token: refreshToken }),
    })
  }, [accessToken])

  const refreshUser = useCallback(async () => {
    if (!accessToken) return
    const u = await fetchMe(accessToken)
    if (u) setUser(u)
  }, [accessToken, fetchMe])

  return (
    <AuthContext.Provider value={{ user, accessToken, isLoading, login, logout, logoutOthers, refreshUser }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (ctx === null) throw new Error('useAuth must be used within an AuthProvider')
  return ctx
}
